/**
 * Implied Confluence Auto-Tag Utility
 *
 * Wave E.1 (v7.5.1) — addresses the friction of "I named my setup after
 * this confluence, do I really have to also tag the chip?"
 *
 * The chip is the source of truth for downstream analyzers — `confluenceComposite`,
 * `setupConfluenceFit`, `setupRuleValidation`, Wave G combos all read
 * `trade.confluences[]`. If the user names a setup "15M 21EMA" but doesn't
 * tag the `15M 21 EMA` chip, those analyzers can't see the relationship,
 * and the rule validator misfires "missing recommended" warnings on the
 * very thing the setup is named after.
 *
 * This helper closes the gap. When the user defines a setup, they can pin
 * a set of `impliedConfluences` — confluence IDs that should automatically
 * apply to every trade with this setup. On save (manual or JournifiSync),
 * those IDs are merged into the trade's confluences array.
 *
 * Semantics — distinct from `recommendedConfluences`:
 *   - `recommendedConfluences` is ADVISORY: "you should look for these
 *     when taking this setup; Coach warns if missing." User-decidable.
 *   - `impliedConfluences` is DEFINITIONAL: "these confluences are inherent
 *     to the setup by name (e.g., the 15M 21EMA setup ALWAYS involves the
 *     15M 21EMA chip)." Auto-applied, not user-decided per trade.
 *
 * Behavior contract:
 *   - Pure: returns a new trade object, never mutates
 *   - Idempotent: never adds a chip already present
 *   - Safe: no-op when setup missing, setup definition missing, or
 *     impliedConfluences absent/empty
 *   - Preserves user explicit chips: only ADDS, never removes
 *   - Order-stable: appends new chips at the end so the user's
 *     existing chip order is preserved
 */

/**
 * Apply implied confluences for a single trade based on its setup.
 *
 * @param {Object} trade           Trade object. Must have `setup` (string name).
 * @param {Object} setupDetailsMap Object keyed by setup name. Each entry may have
 *                                 `impliedConfluences: string[]` (array of confluence IDs).
 *
 * @returns {Object} New trade with implied chips merged into `confluences`.
 *                   Returns the original trade unchanged when no work is needed.
 */
export const applyImpliedConfluences = (trade, setupDetailsMap) => {
  if (!trade || typeof trade !== 'object') return trade;
  if (!trade.setup || typeof trade.setup !== 'string') return trade;
  if (!setupDetailsMap || typeof setupDetailsMap !== 'object') return trade;

  const setupDef = setupDetailsMap[trade.setup];
  if (!setupDef) return trade;

  const implied = setupDef.impliedConfluences;
  if (!Array.isArray(implied) || implied.length === 0) return trade;

  const existing = Array.isArray(trade.confluences) ? trade.confluences : [];
  const existingSet = new Set(existing);

  const toAdd = [];
  for (const id of implied) {
    if (!id || typeof id !== 'string') continue;
    if (existingSet.has(id)) continue;
    toAdd.push(id);
  }

  if (toAdd.length === 0) return trade;

  return {
    ...trade,
    confluences: [...existing, ...toAdd],
  };
};

/**
 * Bulk variant — apply to every trade in an array.
 *
 * @param {Array}  trades           Trade objects.
 * @param {Object} setupDetailsMap  Setup name → definition map.
 * @returns {Object} { trades: newArray, changedCount: number }
 *                   `trades` is a new array; unchanged trades keep
 *                   their original references for shallow-eq stability.
 */
export const applyImpliedConfluencesToTrades = (trades, setupDetailsMap) => {
  if (!Array.isArray(trades) || trades.length === 0) {
    return { trades: trades || [], changedCount: 0 };
  }
  let changedCount = 0;
  const next = trades.map(t => {
    const updated = applyImpliedConfluences(t, setupDetailsMap);
    if (updated !== t) changedCount++;
    return updated;
  });
  return { trades: next, changedCount };
};

/**
 * Diff helper — given a before/after trade pair, return the chip IDs that
 * were added by the auto-tag. Used by the UI to surface "auto-applied X" toasts.
 */
export const impliedConfluenceDiff = (beforeTrade, afterTrade) => {
  const before = new Set(Array.isArray(beforeTrade?.confluences) ? beforeTrade.confluences : []);
  const after  = Array.isArray(afterTrade?.confluences) ? afterTrade.confluences : [];
  return after.filter(id => !before.has(id));
};

export default applyImpliedConfluences;
